<template>
  <!-- Issue #29 — Contador de notas con progreso (tarea 8.5) -->
  <div class="notes-counter" role="status" aria-label="Progreso de notas">
    <!-- Estadísticas -->
    <div class="counter-stats">
      <div class="stat">
        <span class="stat-num" :class="total > 0 ? 'num--active' : ''">{{ total }}</span>
        <span class="stat-label">Notas creadas</span>
      </div>
      <div class="stat">
        <span class="stat-num num--required">{{ required }}</span>
        <span class="stat-label">Obligatorias</span>
      </div>
      <div class="stat">
        <span class="stat-num num--optional">{{ optional }}</span>
        <span class="stat-label">Opcionales</span>
      </div>
    </div>

    <!-- Barra de progreso -->
    <div class="progress-section">
      <div class="progress-label">
        <span>Cobertura obligatoria</span>
        <span class="progress-pct" :class="progressClass">{{ coveragePct }}%</span>
      </div>
      <div
        class="progress-track"
        role="progressbar"
        :aria-valuenow="coveragePct"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div
          class="progress-fill"
          :class="progressClass"
          :style="{ width: `${coveragePct}%` }"
        />
      </div>
      <p class="progress-hint">{{ progressHint }}</p>
    </div>

    <!-- Alertas -->
    <div v-if="missingTypes.length" class="missing-alert" role="alert">
      <i class="pi pi-exclamation-triangle" aria-hidden="true" />
      <span>Faltan: {{ missingTypes.join(', ') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// ── Props ─────────────────────────────────────────────────────────────────────

interface Note {
  note_type?: string
  [key: string]: unknown
}

const props = defineProps<{
  notes: Note[]
}>()

// ── Tipos obligatorios según NIIF ─────────────────────────────────────────────

const REQUIRED_TYPES = [
  'general',
  'politicas_contables',
  'efectivo',
  'cuentas_cobrar',
  'inventarios',
]

const REQUIRED_LABELS: Record<string, string> = {
  general:           'General',
  politicas_contables: 'Políticas contables',
  efectivo:          'Efectivo',
  cuentas_cobrar:    'Ctas. por cobrar',
  inventarios:       'Inventarios',
}

// ── Computed ──────────────────────────────────────────────────────────────────

const total    = computed(() => props.notes.length)
const required = computed(() =>
  props.notes.filter(n => REQUIRED_TYPES.includes(n.note_type ?? '')).length,
)
const optional = computed(() => total.value - required.value)

const coveredTypes = computed(() =>
  REQUIRED_TYPES.filter(t => props.notes.some(n => n.note_type === t)),
)

const missingTypes = computed(() =>
  REQUIRED_TYPES
    .filter(t => !coveredTypes.value.includes(t))
    .map(t => REQUIRED_LABELS[t] ?? t),
)

const coveragePct = computed(() =>
  REQUIRED_TYPES.length === 0
    ? 100
    : Math.round((coveredTypes.value.length / REQUIRED_TYPES.length) * 100),
)

const progressClass = computed(() => {
  if (coveragePct.value >= 100) return 'fill--good'
  if (coveragePct.value >= 60)  return 'fill--warning'
  return 'fill--bad'
})

const progressHint = computed(() => {
  if (coveragePct.value >= 100) return '✅ Cobertura obligatoria completa.'
  if (missingTypes.value.length)
    return `📋 ${missingTypes.value.length} nota${missingTypes.value.length > 1 ? 's' : ''} obligatoria${missingTypes.value.length > 1 ? 's' : ''} pendiente${missingTypes.value.length > 1 ? 's' : ''}.`
  return 'Agrega las notas obligatorias según NIC 1 párr. 112.'
})
</script>

<style scoped>
.notes-counter {
  padding: 0.875rem 1rem;
  background: var(--p-surface-50, #f9fafb);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.counter-stats {
  display: flex;
  gap: 1.5rem;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
}

.stat-num {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--p-text-muted-color, #9ca3af);
  line-height: 1;
}

.num--active  { color: #6366f1; }
.num--required { color: #22c55e; }
.num--optional { color: #f59e0b; }

.stat-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-muted-color, #9ca3af);
}

.progress-section { display: flex; flex-direction: column; gap: 0.3rem; }

.progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-text-muted-color, #4b5563);
}

.progress-pct { font-weight: 800; }
.fill--good   .progress-pct, .progress-pct.fill--good   { color: #22c55e; }
.fill--warning .progress-pct, .progress-pct.fill--warning { color: #f59e0b; }
.fill--bad    .progress-pct, .progress-pct.fill--bad    { color: #ef4444; }

.progress-track {
  height: 8px;
  background: var(--p-surface-200, #e5e7eb);
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.fill--good    { background: #22c55e; }
.fill--warning { background: #f59e0b; }
.fill--bad     { background: #ef4444; }

.progress-hint {
  margin: 0;
  font-size: 0.72rem;
  color: var(--p-text-muted-color, #6b7280);
}

.missing-alert {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #fef9c3;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: #78350f;
}

:root[data-theme='dark'] .notes-counter, .dark .notes-counter {
  background: var(--p-surface-700, #374151);
  border-color: var(--p-surface-600, #4b5563);
}
</style>
